// services/_base/defineService.ts

import { createDocumentService } from '@/services/common/documentService'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'
import type { StoreKey } from '@farmfree/common/constants/collections'

/**
 * 표준 서비스 인터페이스
 */
export interface StandardService<T> {
  getAll(companyId: string): Promise<ApiResponse<T[]>>
  getModified(companyId: string, since: number): Promise<ApiResponse<T[]>>
  getById(companyId: string, itemId: string): Promise<ApiResponse<T>>
  saveItem(companyId: string, item: T): Promise<ApiResponse<{ id: string }>>
  saveItems(companyId: string, items: T[]): Promise<ApiResponse>
  deleteItem(companyId: string, itemId: string): Promise<ApiResponse>
  getDeleted(companyId: string): Promise<ApiResponse<string[]>>
}

/**
 * 서비스 정의 설정
 */
export interface ServiceDefinition {
  /** 서비스명 (예: 'product', 'category') */
  serviceName: StoreKey
}

/**
 * 표준 서비스 팩토리
 * 모든 표준 CRUD 서비스를 일관된 방식으로 생성
 *
 * @example
 * ```typescript
 * export const createProductService = defineService<Product>({
 *   serviceName: 'product'
 * })
 * ```
 */
export function defineService<T extends { id: string }>(
  definition: ServiceDefinition
): (mode: 'admin' | 'guest') => StandardService<T> {
  const { serviceName } = definition

  return (mode: 'admin' | 'guest') => {
    const documentService = createDocumentService<T>(serviceName, mode)

    return {
      async getAll(companyId: string) {
        return await documentService.getAll(companyId)
      },

      async getModified(companyId: string, since: number) {
        return await documentService.getAll(companyId, since)
      },

      async getById(companyId: string, itemId: string) {
        return await documentService.getOne(companyId, itemId)
      },

      async saveItem(companyId: string, item: T) {
        return await documentService.save(companyId, item)
      },

      async saveItems(companyId: string, items: T[]) {
        return await documentService.saveMany(companyId, items)
      },

      async deleteItem(companyId: string, itemId: string) {
        return await documentService.deleteItem(companyId, itemId)
      },

      async getDeleted(companyId: string) {
        return await documentService.getDeleted(companyId)
      },
    }
  }
}

/**
 * 서비스 정의 상수 (중앙 관리)
 */
export const SERVICE_DEFINITIONS = {
  product: { serviceName: 'product' as StoreKey },
  category: { serviceName: 'category' as StoreKey },
  option: { serviceName: 'option' as StoreKey },
  optionGroup: { serviceName: 'optionGroup' as StoreKey },
  marketplace: { serviceName: 'marketplace' as StoreKey },
  courierSaas: { serviceName: 'courierSaas' as StoreKey },
  smsTemplate: { serviceName: 'smsTemplate' as StoreKey },
  bankSettings: { serviceName: 'bankSettings' as StoreKey },
  manualInput: { serviceName: 'manualInput' as StoreKey },
  activeOrder: { serviceName: 'orderPen' as StoreKey },
  consignor: { serviceName: 'consignor' as StoreKey },
  consignmentBatch: { serviceName: 'consignmentBatch' as StoreKey },
  senderPreset: { serviceName: 'senderPreset' as StoreKey },
  paymentMatchGroup: { serviceName: 'paymentMatchGroup' as StoreKey },
  splitRegion: { serviceName: 'splitRegion' as StoreKey },
} as const
